/**
 * Optimiciti API Specification
 * Welcome to the Beta release of the Optimiciti API. This API allows registered users to make direct calls to our optimization solvers via a web request.  Use of the API requires acceptance of our terms of service.  Some useful links: - [Optimiciti Homepage](https://optimiciti.com) - [Optimiciti API Access Plans](https://optimiciti.com/pricing)
 *
 * The version of the OpenAPI document: 0.0.2
 * Contact: api@optimiciti.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 *
 */

import ApiClient from '../ApiClient';
import CostSettings from './CostSettings';
import Location from './Location';
import ProcessUploadedTravelCostRequestUploadedDataInner from './ProcessUploadedTravelCostRequestUploadedDataInner';
import TravelCost from './TravelCost';
import VehicleType from './VehicleType';

/**
 * The ProcessUploadedTravelCostRequest model module.
 * @module model/ProcessUploadedTravelCostRequest
 * @version 0.0.2
 */
class ProcessUploadedTravelCostRequest {
    /**
     * Constructs a new <code>ProcessUploadedTravelCostRequest</code>.
     * A request to process uploaded travel cost data.
     * @alias module:model/ProcessUploadedTravelCostRequest
     * @param uploadedData {Array.<module:model/ProcessUploadedTravelCostRequestUploadedDataInner>} An array of objects representing the uploaded travel cost data. Each object should contain the necessary fields to process the travel costs.
     * @param locations {Array.<module:model/Location>} The locations to use for processing the uploaded travel cost data.
     * @param travelCosts {Array.<module:model/TravelCost>} The travel costs to use for processing the uploaded travel cost data.
     * @param overwriteExistingEntries {Boolean} Whether to overwrite existing travel cost entries with the uploaded data. If true, existing entries will be replaced with the new data. If false, new entries will be added without modifying existing ones.
     */
    constructor(uploadedData, locations, travelCosts, overwriteExistingEntries) { 
        
        ProcessUploadedTravelCostRequest.initialize(this, uploadedData, locations, travelCosts, overwriteExistingEntries);
    }

    /**
     * Initializes the fields of this object.
     * This method is used by the constructors of any subclasses, in order to implement multiple inheritance (mix-ins).
     * Only for internal use.
     */
    static initialize(obj, uploadedData, locations, travelCosts, overwriteExistingEntries) { 
        obj['UploadedData'] = uploadedData;
        obj['Locations'] = locations;
        obj['TravelCosts'] = travelCosts;
        obj['overwriteExistingEntries'] = overwriteExistingEntries || true;
    }

    /**
     * Constructs a <code>ProcessUploadedTravelCostRequest</code> from a plain JavaScript object, optionally creating a new instance.
     * Copies all relevant properties from <code>data</code> to <code>obj</code> if supplied or a new instance if not.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @param {module:model/ProcessUploadedTravelCostRequest} obj Optional instance to populate.
     * @return {module:model/ProcessUploadedTravelCostRequest} The populated <code>ProcessUploadedTravelCostRequest</code> instance.
     */
    static constructFromObject(data, obj) {
        if (data) {
            obj = obj || new ProcessUploadedTravelCostRequest();

            if (data.hasOwnProperty('UploadedData')) {
                obj['UploadedData'] = ApiClient.convertToType(data['UploadedData'], [ProcessUploadedTravelCostRequestUploadedDataInner]);
            }
            if (data.hasOwnProperty('Locations')) {
                obj['Locations'] = ApiClient.convertToType(data['Locations'], [Location]);
            }
            if (data.hasOwnProperty('TravelCosts')) {
                obj['TravelCosts'] = ApiClient.convertToType(data['TravelCosts'], [TravelCost]);
            }
            if (data.hasOwnProperty('VehicleTypes')) {
                obj['VehicleTypes'] = ApiClient.convertToType(data['VehicleTypes'], [VehicleType]);
            }
            if (data.hasOwnProperty('costSettings')) {
                obj['costSettings'] = CostSettings.constructFromObject(data['costSettings']);
            }
            if (data.hasOwnProperty('overwriteExistingEntries')) {
                obj['overwriteExistingEntries'] = ApiClient.convertToType(data['overwriteExistingEntries'], 'Boolean');
            }
        }
        return obj;
    }

    /**
     * Validates the JSON data with respect to <code>ProcessUploadedTravelCostRequest</code>.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @return {boolean} to indicate whether the JSON data is valid with respect to <code>ProcessUploadedTravelCostRequest</code>.
     */
    static validateJSON(data) {
        // check to make sure all required properties are present in the JSON string
        for (const property of ProcessUploadedTravelCostRequest.RequiredProperties) {
            if (!data.hasOwnProperty(property)) {
                throw new Error("The required field `" + property + "` is not found in the JSON data: " + JSON.stringify(data));
            }
        }
        if (data['UploadedData']) { // data not null
            // ensure the json data is an array
            if (!Array.isArray(data['UploadedData'])) {
                throw new Error("Expected the field `UploadedData` to be an array in the JSON data but got " + data['UploadedData']);
            }
            // validate the optional field `UploadedData` (array)
            for (const item of data['UploadedData']) {
                ProcessUploadedTravelCostRequestUploadedDataInner.validateJSON(item);
            };
        }
        if (data['Locations']) { // data not null
            // ensure the json data is an array
            if (!Array.isArray(data['Locations'])) {
                throw new Error("Expected the field `Locations` to be an array in the JSON data but got " + data['Locations']);
            }
            // validate the optional field `Locations` (array)
            for (const item of data['Locations']) {
                Location.validateJSON(item);
            };
        }
        if (data['TravelCosts']) { // data not null
            // ensure the json data is an array
            if (!Array.isArray(data['TravelCosts'])) {
                throw new Error("Expected the field `TravelCosts` to be an array in the JSON data but got " + data['TravelCosts']);
            }
            // validate the optional field `TravelCosts` (array)
            for (const item of data['TravelCosts']) {
                TravelCost.validateJSON(item);
            };
        }
        if (data['VehicleTypes']) { // data not null
            // ensure the json data is an array
            if (!Array.isArray(data['VehicleTypes'])) {
                throw new Error("Expected the field `VehicleTypes` to be an array in the JSON data but got " + data['VehicleTypes']);
            }
            // validate the optional field `VehicleTypes` (array)
            for (const item of data['VehicleTypes']) {
                VehicleType.validateJSON(item);
            };
        }
        // validate the optional field `costSettings`
        if (data['costSettings']) { // data not null
          CostSettings.validateJSON(data['costSettings']);
        }

        return true;
    }


}

ProcessUploadedTravelCostRequest.RequiredProperties = ["UploadedData", "Locations", "TravelCosts", "overwriteExistingEntries"];

/**
 * An array of objects representing the uploaded travel cost data. Each object should contain the necessary fields to process the travel costs.
 * @member {Array.<module:model/ProcessUploadedTravelCostRequestUploadedDataInner>} UploadedData
 */
ProcessUploadedTravelCostRequest.prototype['UploadedData'] = undefined;

/**
 * The locations to use for processing the uploaded travel cost data.
 * @member {Array.<module:model/Location>} Locations
 */
ProcessUploadedTravelCostRequest.prototype['Locations'] = undefined;

/**
 * The travel costs to use for processing the uploaded travel cost data.
 * @member {Array.<module:model/TravelCost>} TravelCosts
 */
ProcessUploadedTravelCostRequest.prototype['TravelCosts'] = undefined;

/**
 * The vehicle types to use for processing the uploaded travel cost data. Required only if the travel costs are dependent on vehicle types.
 * @member {Array.<module:model/VehicleType>} VehicleTypes
 */
ProcessUploadedTravelCostRequest.prototype['VehicleTypes'] = undefined;

/**
 * @member {module:model/CostSettings} costSettings
 */
ProcessUploadedTravelCostRequest.prototype['costSettings'] = undefined;

/**
 * Whether to overwrite existing travel cost entries with the uploaded data. If true, existing entries will be replaced with the new data. If false, new entries will be added without modifying existing ones.
 * @member {Boolean} overwriteExistingEntries
 * @default true
 */
ProcessUploadedTravelCostRequest.prototype['overwriteExistingEntries'] = true;






export default ProcessUploadedTravelCostRequest;

